import * as THREE from "three"
import React, { useEffect, useMemo, useRef } from "react"
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader"
import { Unit as BaseUnit } from "../../shared/types"
import { useGLTF } from "../../shared/useGLTF"
import gsap from "gsap"
import { useThree } from "@react-three/fiber"

interface Unit extends BaseUnit {
  geometry?: THREE.BufferGeometry
  center?: THREE.Vector3
  active: boolean
}

type OverlayGeometryProps = {
  glbSrc: string
  data: Unit[]
  handleClickMesh: (unit: Unit) => void
  onSelectedUnitChange: (unit: Unit) => void
  selectedUnit: Unit | null
  colours: any
  opacity?: number
  noAnimation?: boolean
}

const OverlayGeometry: React.FC<OverlayGeometryProps> = ({
  glbSrc,
  data,
  handleClickMesh,
  onSelectedUnitChange,
  selectedUnit,
  colours,
  opacity,
  noAnimation = false,
}) => {
  const gltf = useGLTF(glbSrc) as GLTF

  const geoArray: Unit[] = useMemo(() => {
    const meshes: Unit[] = []

    gltf.scene.traverse((node) => {
      if ((node as THREE.Mesh).isMesh) {
        const mesh = node as THREE.Mesh<THREE.BufferGeometry>
        const unitData = data.find((unit) => unit.unitName === mesh.name)

        meshes.push({
          ...unitData,
          geometry: mesh.geometry,
          center: mesh.geometry.boundingSphere?.center,
        })
      }
    })

    return meshes
  }, [gltf, data])

  useEffect(() => {
    if (selectedUnit) {
      const selectedUnitData = geoArray.find(
        (unit) => unit.unitName === selectedUnit.unitName,
      )

      if (selectedUnitData) {
        onSelectedUnitChange(selectedUnitData)
      } else {
        console.warn(
          `Center not found for selected unit: ${selectedUnit.unitName}`,
        )
      }
    }
  }, [geoArray, selectedUnit, onSelectedUnitChange])

  return (
    <group>
      {geoArray.map((unitData, index) => (
        <OverlayElement
          colours={colours}
          key={index}
          unitData={unitData}
          noAnimation={noAnimation}
          opacity={opacity}
          handleClickMesh={handleClickMesh}
          isSelected={selectedUnit?.unitName === unitData.unitName}
        />
      ))}
    </group>
  )
}

type OverlayElementProps = {
  unitData: Unit
  handleClickMesh: (unit: Unit) => void
  isSelected: boolean
  colours: any
  opacity?: number
  noAnimation?: boolean
}

const OverlayElement: React.FC<OverlayElementProps> = React.memo(
  ({
    unitData,
    handleClickMesh,
    isSelected,
    colours,
    opacity = 0.6,
    noAnimation = false,
  }) => {
    const matRef = useRef<THREE.MeshMatcapMaterial>(null!)
    const { invalidate } = useThree()

    const isActive = unitData.active

    useEffect(() => {
      gsap.to(matRef.current, {
        delay: noAnimation ? 0.4 : 0,
        opacity: isActive ? opacity : 0,
        duration: noAnimation ? 0 : 0.5,
        onComplete: () => {
          invalidate()
        },
      })
    }, [isActive])

    useEffect(() => {
      gsap.to(matRef.current.color, {
        delay: noAnimation ? 0.4 : 0,
        r: isSelected ? colours.on.r : colours.off.r,
        g: isSelected ? colours.on.g : colours.off.g,
        b: isSelected ? colours.on.b : colours.off.b,
        duration: noAnimation ? 0 : 0.5,
        onComplete: () => {
          invalidate()
        },
      })
    }, [isSelected])

    return (
      <mesh
        onClick={(e) => {
          e.stopPropagation()
          handleClickMesh(unitData)
        }}
        onPointerOver={(e) => {
          document.body.style.cursor = "pointer"
        }}
        onPointerOut={(e) => {
          document.body.style.cursor = "auto"
        }}
        renderOrder={1}
        geometry={unitData.geometry}
      >
        <meshMatcapMaterial
          ref={matRef}
          opacity={0}
          side={THREE.FrontSide}
          toneMapped={true}
          transparent={true}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>
    )
  },
  (prevProps, nextProps) =>
    prevProps.unitData.active === nextProps.unitData.active &&
    prevProps.isSelected === nextProps.isSelected,
)

export default OverlayGeometry
